import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();
    const refParam = new URLSearchParams(loc.search).get("ref") || "";
    const [form, setForm] = useState({
        name: "",
        agency_name: "",
        email: "",
        password: "",
        referral_code: refParam.toUpperCase(),
        // Sovereign data points (Director directive 2026.02 · Privacy Perimeter)
        linkedin_url: "",
        role_request: "partner",
        location_state: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [refInfo, setRefInfo] = useState(null);

    const checkReferral = async (code) => {
        if (!code) { setRefInfo(null); return; }
        try {
            const { data } = await api.get(`/referrals/check/${code.toUpperCase()}`);
            setRefInfo({ ok: true, agency: data.agency_name });
        } catch {
            setRefInfo({ ok: false });
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = { ...form };
            if (!payload.referral_code) delete payload.referral_code;
            if (!payload.linkedin_url?.trim()) delete payload.linkedin_url;
            if (!payload.location_state?.trim()) delete payload.location_state;
            if (!payload.role_request || payload.role_request === "partner") delete payload.role_request;
            await register(payload);
            toast.success("Fleet boarded. Welcome, Partner.");
            navigate("/app/boarding");
        } catch (ex) {
            setErr(ex.message);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { if (refParam) checkReferral(refParam); /* eslint-disable-next-line */ }, []);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6 grain-overlay relative">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
                <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(40_60%_45%/0.12)] blur-[120px]" />
                <div className="absolute bottom-[-30%] left-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(153_50%_25%/0.25)] blur-[120px]" />
            </div>

            <div className="relative w-full max-w-md glass rounded-2xl p-8 md:p-10">
                <Link to="/" className="font-heading text-3xl gold-gradient-text block mb-1">EIGOO</Link>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-8">Board the Fleet</div>

                <form onSubmit={onSubmit} className="space-y-4">
                    {["name", "agency_name", "email", "password"].map((field) => (
                        <div key={field}>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">
                                {field.replace("_", " ")}
                                {field !== "agency_name" && <span className="text-[hsl(350_75%_70%)] ml-1">*</span>}
                            </label>
                            <input
                                type={field === "password" ? "password" : field === "email" ? "email" : "text"}
                                value={form[field]}
                                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                                required={field !== "agency_name"}
                                data-testid={`register-${field}`}
                                className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors"
                            />
                        </div>
                    ))}

                    {/* Sovereign data points — LinkedIn for vetting + role tier (Privacy Perimeter) */}
                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">
                            LinkedIn profile <span className="text-[hsl(350_75%_70%)] ml-1">*</span>
                        </label>
                        <input
                            type="url"
                            value={form.linkedin_url}
                            onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                            required
                            data-testid="register-linkedin_url"
                            placeholder="https://www.linkedin.com/in/your-handle"
                            className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors"
                        />
                        <div className="mt-1 text-[10px] text-muted-foreground">
                            Vetting by the Director · LinkedIn is your identity proof on the Fleet.
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Role tier</label>
                            <select
                                value={form.role_request}
                                onChange={(e) => setForm({ ...form, role_request: e.target.value })}
                                data-testid="register-role_request"
                                className="mt-1 w-full px-3 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors"
                            >
                                <option value="partner">Partner · default</option>
                                <option value="field_operative">Field Operative</option>
                                <option value="founding_privateer">Founding Privateer*</option>
                                <option value="td_commander">TD Commander*</option>
                                <option value="sports_admiral">Sports Admiral*</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">State</label>
                            <input
                                type="text"
                                value={form.location_state}
                                onChange={(e) => setForm({ ...form, location_state: e.target.value.toUpperCase().slice(0, 2) })}
                                data-testid="register-location_state"
                                placeholder="NC"
                                maxLength={2}
                                className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors font-mono uppercase"
                            />
                        </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground -mt-2">
                        * Elevated tiers require Director approval. You'll start as Partner; the Director will promote you after review.
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Referral code (optional)</label>
                        <input
                            type="text"
                            value={form.referral_code}
                            onChange={(e) => {
                                const v = e.target.value.toUpperCase();
                                setForm({ ...form, referral_code: v });
                            }}
                            onBlur={(e) => checkReferral(e.target.value)}
                            data-testid="register-referral_code"
                            maxLength={10}
                            className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] text-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] transition-colors font-mono"
                            placeholder="e.g. MJTS378J"
                        />
                        {refInfo && refInfo.ok && (
                            <div className="mt-1 text-xs text-[hsl(45_85%_68%)]" data-testid="referral-valid">
                                Referred by {refInfo.agency}
                            </div>
                        )}
                        {refInfo && !refInfo.ok && (
                            <div className="mt-1 text-xs text-[hsl(350_75%_70%)]" data-testid="referral-invalid">
                                Code not found
                            </div>
                        )}
                    </div>

                    {err && (
                        <div className="text-sm text-[hsl(350_80%_70%)] bg-[hsl(350_60%_20%/0.4)] border border-[hsl(350_60%_40%/0.3)] rounded-md px-3 py-2" data-testid="register-error">
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="register-submit"
                        className="w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                    >
                        {busy ? "Signing the covenant..." : "Board the Fleet"}
                    </button>
                </form>

                <div className="mt-6 text-center text-sm text-muted-foreground">
                    Already a partner?{" "}
                    <Link to="/login" className="text-[hsl(45_85%_68%)] hover:underline">Sign in</Link>
                </div>
            </div>
        </div>
    );
}
